import React from 'react';
import {
    Accordion,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';

import * as S from './Specifications.style';
import Downloads from '@modules/product/component/downloads/Downloads';
import type LibraryDownload from '@modules/product/type/library-download';

import CollapsibleContainer from './CollapsibleContainer';
import { AccordionTitle, AccordionContent } from '@fluentui/react-northstar';
import { AngleUp, AngleDown } from '@archipro-design/icons';
import type { TrackingEvent } from '@archipro-website/tracker';
import { useTracker } from '@archipro-website/tracker';
import { useSearchParams } from 'react-router-dom';
import { useRef, useEffect } from 'react';

interface DownloadsAccordionData {
    Professional: {
        Title: string;
    };
    CanPurchase: boolean;
    LibraryDownloads: LibraryDownload[];
}

interface DownloadsAccordionProps {
    data: DownloadsAccordionData;
}

const DownloadsAccordion = ({ data }: DownloadsAccordionProps) => {
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const trackingPrefix = 'product';
    const [searchParams] = useSearchParams();

    const downloadsSectionRef = useRef<HTMLDivElement>(null);
    const showDownloads = searchParams.get('openDownloadModal');

    useEffect(() => {
        if (downloadsSectionRef.current && showDownloads) {
            downloadsSectionRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [downloadsSectionRef, showDownloads]);

    const [activeIndex, setActiveIndex] = React.useState<number[] | null>(
        () => {
            const desktopDefaultIndexes = showDownloads ? [0] : null;
            const moduleDefaultIndexes = null;
            return mobile ? moduleDefaultIndexes : desktopDefaultIndexes;
        }
    );

    const tabItems = React.useMemo(() => {
        const panels = [];

        if (data.LibraryDownloads.length) {
            panels.push({
                key: `${trackingPrefix}-downloads-tab`,
                title:
                    'Downloads' +
                    (!mobile ? '' : ` (${data.LibraryDownloads.length})`),
                content: {
                    children: <Downloads data={data} />,
                    key: `${trackingPrefix}-downloads-content`,
                },
            });
        }

        return panels;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
        <div ref={downloadsSectionRef} className={css(S.Downloads)}>
            <Accordion className={css(S.AccordionContainer)}>
                {tabItems.map((p, i) => (
                    <DownloadsAccordionItem
                        key={'product-spec-' + i}
                        i={i}
                        p={p}
                        activeIndex={activeIndex}
                        setActiveIndex={setActiveIndex}
                    />
                ))}
            </Accordion>
        </div>
    );
};

const DownloadsAccordionItem = ({
    i,
    p,
    activeIndex,
    setActiveIndex,
}: {
    i: number;
    p: {
        key: string;
        title: string;
        content: {
            children: JSX.Element;
            key: string;
        };
    };
    activeIndex: number[] | null;
    setActiveIndex: React.Dispatch<React.SetStateAction<number[] | null>>;
}) => {
    const active = activeIndex?.includes(i) ?? false;
    const { css } = useStyles({ active });
    const tracker = useTracker();

    return (
        <React.Fragment>
            <AccordionTitle
                active={activeIndex?.includes(i)}
                className={css(S.AccordionTitleWrapper)}
                index={i}
                onClick={() => {
                    if (!activeIndex?.includes(i)) {
                        let event: TrackingEvent | null = null;
                        if (p.title === 'Details') {
                            event = 'ProductDetailsExpandClick';
                        } else if (p.title === 'Information') {
                            event = 'ProductInformationExpandClick';
                        }
                        if (event) {
                            tracker.log(event, {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                            });
                        }
                    }
                    setActiveIndex((current) => {
                        if (current) {
                            if (current.includes(i)) {
                                return current.filter((c) => c !== i);
                            } else {
                                return [...current, i];
                            }
                        }
                        return [i];
                    });
                }}
                indicator={
                    activeIndex?.includes(i) ? (
                        <AngleUp className={css(S.AccordionIcon)} />
                    ) : (
                        <AngleDown className={css(S.AccordionIcon)} />
                    )
                }
                content={{
                    className: css(S.AccordionTitle),
                    content: String(p.title),
                }}
            />
            <CollapsibleContainer visible={activeIndex?.includes(i) ?? false}>
                <AccordionContent
                    {...p.content}
                    active={true}
                ></AccordionContent>
            </CollapsibleContainer>
        </React.Fragment>
    );
};

export default DownloadsAccordion;
